import OOT from "Constants/services/oot";
import CONFIG_API from "Constants/services/configurator";
import { setMarketplaceSid } from "Store/contentState/marketplaceSlice";
import _ from 'lodash';
import { groupProductsIntoCategory } from './productCategoryUtils';
import { getBundleDurationMonths, TYPE_OF_BUNDLE, updateProductTotalPriceAndAdditionalItems } from "../Utils/BundleHelper";
import { store } from "Store/mainStore";

/**
 * Service functions that can be used in thunks (non-React contexts)
 * These mirror the hook functionality but don't use React hooks
 */

/**
 * Get submission data for configurator API calls
 */
export const getSubmissionDataService = (params: any,do_you_have_the_mxie_server_value= "No") => {
    const dynamicColumn_appname = params?.configurator_app_name || "";
    const compactkit_type_appname = "compact_id_type_" + dynamicColumn_appname;
    let submissiondata = {
        "contract_duration_months": params?.contract_duration_months ?? getBundleDurationMonths(params),
        "commercial_model": "Opex",
        // [dynamicColumn_appname]: params?.productQty || 1,
        "georedundancy_1_1_units": "No",
        "connector_switch": "No",
        "do_you_have_the_mxie_server": do_you_have_the_mxie_server_value
    };
    if (params?.selectedEdgeMxieServer) {
        submissiondata["selected_mxie_server"] = params?.selectedEdgeMxieServer;
    }
    if (params?.compact_id_type) {
        submissiondata[compactkit_type_appname] = params?.compact_id_type;
    }
    if (dynamicColumn_appname) {
        submissiondata[dynamicColumn_appname] = params?.productQty || 1;
    }
    return submissiondata;
};

/**
 * Submit form data and get marketplace SID
 */
export const submitFormAndGetSidService = async (params: any, marketplaceSelector: any, dispatch: any) => {
    try {
        let methodType = "POST";
        const solutionName = "marketplace_bundle";
        const sid = marketplaceSelector?.marketplace_sid;
        const status = "draft";
        const basicArgs = `account_id=${params.accountId}&currency=${params.currency}&sales_channel=${params.salesChannel}`;

        if (sid && sid.length !== 0) {
            methodType = "PATCH";
        }
        const type_of_bundle = params?.["detailType"] || "";
        const do_you_have_the_mxie_server_value = type_of_bundle === "mxie" ? "NA" : (store.getState().marketplace?.productBundle?.haveMxieServer || "No");
        const cartDataSend = {
            "status": status,
            "segment": "marketplace_bundle",
            data: getSubmissionDataService(params, do_you_have_the_mxie_server_value)
        };

        const submissionRes = await CONFIG_API.segmentFormSubmission(methodType, solutionName, cartDataSend, sid, basicArgs, true);

        const sidResponse = submissionRes?.data?.sid;
        if (!sidResponse) {
            throw new Error('SID not received from configurator API');
        }

        if (!marketplaceSelector?.marketplace_sid) {
            dispatch(setMarketplaceSid(sidResponse));
        }

        return { sid: sidResponse, basicArgs, response: submissionRes };
    } catch (error) {
        console.error('Error in submitFormAndGetSid:', error);
        throw error;
    }
};

/**
 * Get consumption data from configurator
 */
export const getConsumptionDataService = async (sidResponse: string, basicArgs: string) => {
    try {
        const endpoint = "/api/configurator/segment/ndac_reference_solution/data/consumption/all";
        const method = "GET";
        const args = basicArgs + '&sid=' + sidResponse;

        const resp = await CONFIG_API.configuratorDataCallback(method, endpoint, args, undefined);
        const consum_data = resp?.data || {}
        return { consumptionData: consum_data, consumptionResponse: resp };

    } catch (error) {
        console.error('Error in getConsumptionData:', error);
        throw error;
    }
};

/**
 * Get MXIE server data based on consumption
 */
export const getMxieServerDataService = async (basicArgs: string, consumptionData: any, params: any) => {
    try {
        const mxie_params = basicArgs +
            '&connector_switch=No&geo_redundancy=No' +
            '&vcpu_consumption=' + consumptionData.vcpu +
            '&pod_consumption=' + consumptionData.pod +
            '&memory_consumption=' + consumptionData.memory +
            '&storage_consumption=' + consumptionData.storage;

        const endpoint = "/api/configurator/segment/ndac_reference_solution/field-data/mxie_server";
        const method = "GET";

        const mxie_resp = await CONFIG_API.configuratorDataCallback(method, endpoint, mxie_params, undefined);
        const mxie_data = mxie_resp?.data;

        if (!mxie_data) {
            throw new Error('MXIE data not received from configurator API');
        }

        const mxie_available = _.filter(mxie_data, (mx) => mx.isAvailable === "true");

        params.mxieServerIds = mxie_available.map((mx) => mx.sales_kit_id);

        if (!params?.mxieServerIds || !params?.mxieServerIds?.length) {
            return [];
        }

        params.mxieServerIds = Array.from(new Set(params.mxieServerIds as string[]));

        const marketPlaceMxieServersResponse = await OOT.getValidMxieServers(params);
        let marketPlaceMxieServers = [];

        if (marketPlaceMxieServersResponse?.data?.length > 0) {
            for (const mxieServer of marketPlaceMxieServersResponse?.data) {
                const availableMxieServer = mxie_available.filter((mx) => mx.sales_kit_id === mxieServer?.id).map((server) => {
                    server.product_type_id = mxieServer.product_type_id;
                    return server;
                });
                if (availableMxieServer) {
                    marketPlaceMxieServers = marketPlaceMxieServers.concat(availableMxieServer);
                }
            }
        }

        return marketPlaceMxieServers;
    } catch (error) {
        console.error('Error in getMxieServerData:', error);
        throw error;
    }
};

/**
 * Process and categorize MXIE server data
 * @param mxie_available - Array of available MXIE servers
 * @param type_of_bundle - Type of bundle (bundle/mxie)
 */
export const processMxieServerDataService = (
    mxie_available: any[],
    type_of_bundle: string,
    params: any
) => {
    try {
        const productCategoryData = groupProductsIntoCategory(mxie_available, "");

        const mxieEdgeServers = type_of_bundle === TYPE_OF_BUNDLE?.toLowerCase()
            ? [...(productCategoryData?.['Switch'] || []), ...(productCategoryData?.['Edge'] || [])].sort((a, b) => a.list_price - b.list_price)
            : [];

        mxieEdgeServers?.map((server) => {
            server.is_agent_model = 0;
            server.is_deletable = false;
            server.is_dependent_item = true;
            server.parent_product_id = params?.id;
            return server;
        });
        return { productCategoryData, mxieEdgeServers };
    } catch (error) {
        console.error('Error in processMxieServerData:', error);
        throw error;
    }
};

/**
 * Get compact bundle components from Datamart
 */
export const getCompactBundleComponentsService = async (params: any) => {
    try {
        const response = await OOT.getProductBundleDetails(params);
        const components = response?.data?.[0]?.kit_products?.map((component: any) => ({
            ...component,
            id: component?.product_id,
            is_deletable: false,
            is_dependent_item: true,
            priceamount_currencycode: response?.data?.[0]?.priceamount_currencycode || 'NA',
            parent_product_id: params?.id,
        })) || [];

        components[0].description = params.detailType !== 'mxie' ? response?.data?.[0]?.description : response?.data?.[0]?.product_description;
        const configuratorFields = {
            configurator_app_name: response?.data?.[0]?.configurator_app_name
        };
        return { components, configuratorFields };
    } catch (error) {
        console.error('Error in getCompactBundleComponents:', error);
        throw error;
    }
};

/**
 * Generate bundle prerequisites and descriptions
 */
export const generateBundleMetadataService = (type_of_bundle: string, product: any) => {
    // const automatic_additions: string[] = [];
    let preReq: any[] = [];
    const bundleName = product?.compact_id || "";
    if (type_of_bundle.toLowerCase() === TYPE_OF_BUNDLE.toLowerCase()) {
        preReq = [{
            id: 'mxie_server',
            label: 'Do you have the MXIE Server?',
            options: [
                { id: 'Yes', value: 'Yes', checked: true },
                { id: 'No', value: 'No', checked: false }
            ]
        }, {
            id: 'pre_requisite_list',
            values: [
                "Use the MXIE Manager to validate whether your current resources meet the application requirements.",
                `For the ${bundleName} package, a relevant MXIE platform subscription (based on capacity units) will be automatically added to your cart.`
            ]
        }];
    }

    return { preReq };
};

/**
 * Get configurator product bundle details - Main 1st time flow
 */
export const getConfiguratorProductBundleDetailsService = async (params: any, marketplaceSelector: any, dispatch: any) => {
    try {
        const type_of_bundle = params?.["detailType"];
        const { sid, basicArgs, response } = await submitFormAndGetSidService(params, marketplaceSelector, dispatch);
        let consumptionData, mxie_available, productCategoryData, mxieEdgeServers;

        if (type_of_bundle === TYPE_OF_BUNDLE?.toLowerCase()) {
            ({ consumptionData } = await getConsumptionDataService(sid, basicArgs));
            mxie_available = await getMxieServerDataService(basicArgs, consumptionData, params);
            ({ productCategoryData, mxieEdgeServers } = processMxieServerDataService(mxie_available, type_of_bundle, params));
        }

        // ({ preReq } = generateBundleMetadataService(type_of_bundle));

        return {
            mxieEdgeServers,
            // preReq,
            productCategoryData,
            consumptionData,
            sid,
            submissionRes: response
        };
    } catch (error) {
        console.error('Error in getConfiguratorProductBundleDetails:', error);
        throw error;
    }
};

/**
 * Get configurator product bundle details - Main service function for thunks
 * This function decides whether to call the configurator API or not based on mxie_required flag
 */
const getConfiguratorProductBundleResponse = async (params: any, marketplaceSelector: any, dispatch: any) => {
    const mxie_required = params?.["mxie_required"] || false;
    if (mxie_required) {
        return await getConfiguratorProductBundleDetailsService(params, marketplaceSelector, dispatch);
    } else {
        const { sid, response } = await submitFormAndGetSidService(params, marketplaceSelector, dispatch);
        return { mxieEdgeServers: [], submissionRes: response, sid };
    }
}

/**
 * Get complete product bundle details - Main service function for thunks
 */
export const getProductBundleDetailsService = async (params: any, marketplaceSelector: any, dispatch: any) => {
    try {
        const type_of_bundle = params?.["detailType"] || TYPE_OF_BUNDLE?.toLowerCase();
        // Get configurator details response
        const configuratorResponse = await getConfiguratorProductBundleResponse(params, marketplaceSelector, dispatch);
        const { mxieEdgeServers, submissionRes, sid } = configuratorResponse;
        let preReq = [];
        const { components, configuratorFields } = await getCompactBundleComponentsService(params);
        let bundleComponents = components;

        if (submissionRes?.status) {
            let product = marketplaceSelector?.productBundle?.product_details;
            product = { ...product, marketplace_sid: sid };
            preReq = generateBundleMetadataService(type_of_bundle, product)?.preReq || [];
            // Get mxie from configurator response if type_of_bundle is mxie
            if (type_of_bundle === 'mxie') {
                const mxieProductDetails = await getMxieProductDetails({ ...params, selectedEdgeMxieServer: params?.id });
                if (mxieProductDetails && mxieProductDetails?.response?.data?.length > 0) {
                    const totalIRP = +mxieProductDetails.response?.data[0].IRP || 0;
                    product.price_amount = totalIRP;
                }
            }

            const configuratorSaleItems = submissionRes?.data?.boqData?.data?.saleItems || [];
            const updatedResponse = await updateProductTotalPriceAndAdditionalItems({ product, configuratorSaleItems, dispatch, bundleComponents: components, type_of_bundle }, marketplaceSelector);
            bundleComponents = updatedResponse.bundleComponents;
        }
        const data = {
            productDescription: bundleComponents?.[0]?.description,
            bundleComponents: bundleComponents,
            prerequisites: preReq,
            configuratorFields,
            mxieEdgeServers,
            // ...(type_of_bundle === 'mxie' ? { automatic_additions } : {})
        };
        return { status: true, data };
    } catch (error) {
        console.error('Error in getProductBundleDetails:', error);
        throw error;
    }
};

/**
 * Submit form data and get marketplace SID
 */
export const getMxieProductDetails = async (params: any) => {
    try {
        const { email } = getState();
        const basicArgs = `_format=json&account_id=${params.accountId}&currency=${params.currency}&sales_channel=${params.salesChannel}&title=${params.selectedEdgeMxieServer}&username=${email}`;
        // /?_format=json&username=tony.r@nokia.com&account_id=null&currency=EUR&sales_channel=RES&owner_email=undefined&validate=false&title=SKNDEDGN1164
        // /api/configurator/search/catalog_items_complete_search_sales_kit/?title=SKNDEDGN1130&account_id=1000001016&currency=EUR&sales_channel=DIS
        const submissionRes = await CONFIG_API.getMxieProductDetailsFromConfigurator(basicArgs);
        return { response: submissionRes };
    } catch (error) {
        console.error('Error in submitFormAndGetSid:', error);
        throw error;
    }
};

const getState = () => {
    // Import your Redux store and selector
    const reduxtStore = store.getState();
    // Placeholder for demonstration:
    const email = reduxtStore.authSlice?.login?.mail || ""; // Replace with actual Redux state value
    const userId = reduxtStore.authSlice?.login?.id || ""; // Get user ID from Redux state
    return { userId, email };
}